// lib/admin/businessesAdmin.ts

import { auth, db } from "@/lib/firebase";
import {
  Timestamp,
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type QuerySnapshot,
} from "firebase/firestore";

export type AdminBusinessStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

export type AdminTaxonomyDisplaySource =
  | ""
  | "official"
  | "owner_suggested"
  | "legacy";

export type AdminTaxonomyStatus = "approved" | "pending" | (string & {});

export type AdminBusiness = {
  id: string;

  /**
   * Permanent support/customer-care account number.
   *
   * Format:
   * - 11 digits total
   * - first 10 digits are random
   * - last digit is the check digit
   *
   * Store as string, never number.
   */
  accountNumber?: string;
  accountNumberCreatedAt?: unknown;

  name?: string;
  slug?: string;
  ownerId?: string | null;

  ownerName?: string;
  ownerPhone?: string;

  phone?: string;
  email?: string;
  businessEmail?: string;

  status?: AdminBusinessStatus;

  isPublished?: boolean;
  isDeleted?: boolean;
  isDeactivated?: boolean;

  isFeatured?: boolean;
  featured?: boolean;
  featuredRank?: number | null;

  isClaimed?: boolean;
  claim?: {
    claimedBy?: string | null;
    claimedAt?: unknown;
    claimExpiresAt?: unknown;
  } | null;

  rejectedReason?: string | null;
  changesRequestMessage?: string | null;

  category?: unknown;
  categories?: unknown;
  categoryId?: string | null;
  categoryName?: string;
  categoryNameNormalized?: string;
  categoryStatus?: AdminTaxonomyStatus;

  subCategory?: unknown;
  subCategoryText?: string;
  subCategories?: unknown;
  subcategories?: unknown;
  subcategory?: unknown;
  subcategoryId?: string | null;
  subcategoryName?: string;
  subcategoryNameNormalized?: string;
  subcategoryStatus?: AdminTaxonomyStatus;

  displayCategory?: string;
  displaySubcategory?: string;
  displayCategorySource?: AdminTaxonomyDisplaySource;
  displaySubcategorySource?: AdminTaxonomyDisplaySource;
  isCategoryOwnerSuggested?: boolean;
  isSubcategoryOwnerSuggested?: boolean;

  updatedAt?: unknown;
  createdAt?: unknown;

  [key: string]: unknown;
};

export type AdminBusinessTaxonomyFields = Partial<
  Pick<
    AdminBusiness,
    | "category"
    | "categories"
    | "categoryName"
    | "displayCategory"
    | "subCategory"
    | "subCategoryText"
    | "subCategories"
    | "subcategories"
    | "subcategory"
    | "subcategoryName"
    | "displaySubcategory"
  >
>;

type ListenOpts = {
  status: "all" | AdminBusinessStatus;
  showDeleted: boolean;
  limitCount?: number;
};

type FindByAccountNumberOpts = {
  showDeleted?: boolean;
  limitCount?: number;
};

const BUSINESS_COLLECTION = "businesses";

const CLAIM_MINUTES = 5;
const DEFAULT_ADMIN_LIMIT = 200;
const ACCOUNT_NUMBER_DIGITS = 11;
const DEFAULT_FEATURED_RANK = 9999;

function uidOrThrow() {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("Not authenticated");
  }

  return uid;
}

function expiresInMinutes(mins: number) {
  return Timestamp.fromMillis(Date.now() + mins * 60 * 1000);
}

function cleanText(value: unknown) {
  if (typeof value !== "string") return "";
  return value.trim();
}

function cleanDisplayText(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => cleanDisplayText(item))
      .filter(Boolean)
      .join(", ");
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return (
      cleanText(record.name) ||
      cleanText(record.label) ||
      cleanText(record.title)
    );
  }

  return cleanText(value);
}

function cleanFirstDisplayText(...values: unknown[]) {
  for (const value of values) {
    const text = cleanDisplayText(value);
    if (text) return text;
  }

  return "";
}

function isPendingTaxonomyStatus(value: unknown) {
  return cleanText(value).toLowerCase() === "pending";
}

export function getAdminBusinessCategoryLabel(
  business: AdminBusinessTaxonomyFields | null | undefined
) {
  return cleanFirstDisplayText(
    business?.categoryName,
    business?.category,
    business?.categories,
    business?.displayCategory
  );
}

export function getAdminBusinessSubcategoryLabel(
  business: AdminBusinessTaxonomyFields | null | undefined
) {
  return cleanFirstDisplayText(
    business?.subcategoryName,
    business?.subCategoryText,
    business?.subCategory,
    business?.subcategory,
    business?.subCategories,
    business?.subcategories,
    business?.displaySubcategory
  );
}

export function shouldShowAdminOwnerSuggestedLabel(
  label: string,
  status: unknown,
  normalizedFlag: unknown
) {
  return (
    cleanText(label).length > 0 &&
    (isPendingTaxonomyStatus(status) || normalizedFlag === true)
  );
}

function resolveTaxonomyDisplay(args: {
  isPending: boolean;
  officialValues: unknown[];
  pendingValues: unknown[];
  legacyValues: unknown[];
}): {
  text: string;
  source: AdminTaxonomyDisplaySource;
  ownerSuggested: boolean;
} {
  const officialText = args.isPending
    ? ""
    : cleanFirstDisplayText(...args.officialValues);

  if (officialText) {
    return {
      text: officialText,
      source: "official",
      ownerSuggested: false,
    };
  }

  const legacyText = args.isPending
    ? ""
    : cleanFirstDisplayText(...args.legacyValues);

  if (legacyText) {
    return {
      text: legacyText,
      source: "legacy",
      ownerSuggested: false,
    };
  }

  const pendingText = cleanFirstDisplayText(...args.pendingValues);

  if (pendingText) {
    return {
      text: pendingText,
      source: "owner_suggested",
      ownerSuggested: true,
    };
  }

  return {
    text: "",
    source: "",
    ownerSuggested: false,
  };
}

export function normalizeAdminBusiness(
  data: Record<string, unknown>,
  id: string
): AdminBusiness {
  const categoryIsPending = isPendingTaxonomyStatus(data.categoryStatus);
  const subcategoryIsPending = isPendingTaxonomyStatus(data.subcategoryStatus);

  const category = resolveTaxonomyDisplay({
    isPending: categoryIsPending,
    officialValues: [data.categoryName],
    pendingValues: categoryIsPending
      ? [
          data.categoryName,
          data.category,
          data.categorySuggestionName,
          data.suggestedCategoryName,
        ]
      : [data.categorySuggestionName, data.suggestedCategoryName],
    legacyValues: [data.category, data.categories],
  });

  let subcategory = resolveTaxonomyDisplay({
    isPending: subcategoryIsPending,
    officialValues: [data.subcategoryName],
    pendingValues: subcategoryIsPending
      ? [
          data.subcategoryName,
          data.subCategoryText,
          data.subCategory,
          data.subcategory,
          data.subcategorySuggestionName,
          data.suggestedSubcategoryName,
        ]
      : [data.subcategorySuggestionName, data.suggestedSubcategoryName],
    legacyValues: [
      data.subcategoryName,
      data.subCategoryText,
      data.subCategory,
      data.subCategories,
      data.subcategories,
      data.subcategory,
    ],
  });

  if (categoryIsPending && subcategory.text) {
    subcategory = {
      ...subcategory,
      source: "owner_suggested",
      ownerSuggested: true,
    };
  }

  return {
    ...(data as Omit<AdminBusiness, "id">),
    id,
    displayCategory: category.text,
    displaySubcategory: subcategory.text,
    displayCategorySource: category.source,
    displaySubcategorySource: subcategory.source,
    isCategoryOwnerSuggested: category.ownerSuggested,
    isSubcategoryOwnerSuggested: subcategory.ownerSuggested,
  };
}

export function normalizeBusinessAccountNumber(value: unknown) {
  return cleanText(value).replace(/\D/g, "");
}

export function isExactBusinessAccountNumber(value: unknown) {
  return normalizeBusinessAccountNumber(value).length === ACCOUNT_NUMBER_DIGITS;
}

function toMillis(value: unknown): number {
  if (!value) return 0;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (
    typeof value === "object" &&
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }

  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().getTime();
  }

  return 0;
}

function sortNewestFirst(a: AdminBusiness, b: AdminBusiness) {
  const aMs = toMillis(a.updatedAt) || toMillis(a.createdAt);
  const bMs = toMillis(b.updatedAt) || toMillis(b.createdAt);

  return bMs - aMs;
}

function sortOldestFirst(a: AdminBusiness, b: AdminBusiness) {
  const aMs = toMillis(a.updatedAt) || toMillis(a.createdAt);
  const bMs = toMillis(b.updatedAt) || toMillis(b.createdAt);

  return aMs - bMs;
}

function normalizeRows(snap: QuerySnapshot<DocumentData>): AdminBusiness[] {
  return snap.docs.map((docSnap) =>
    normalizeAdminBusiness(docSnap.data(), docSnap.id)
  );
}

function filterDeleted(rows: AdminBusiness[], showDeleted: boolean) {
  if (showDeleted) return rows;

  return rows.filter((business) => business.isDeleted !== true);
}

/**
 * Exact admin/support lookup by business account number.
 *
 * This searches businesses/{businessId}.accountNumber directly.
 * Do not use businessSearch for account-number lookup because account
 * numbers should not be part of the public search index.
 */
export async function findBusinessesAdminByAccountNumber(
  accountNumber: string,
  opts: FindByAccountNumberOpts = {}
): Promise<AdminBusiness[]> {
  uidOrThrow();

  const normalizedAccountNumber = normalizeBusinessAccountNumber(accountNumber);

  if (normalizedAccountNumber.length !== ACCOUNT_NUMBER_DIGITS) {
    return [];
  }

  const snap = await getDocs(
    query(
      collection(db, BUSINESS_COLLECTION),
      where("accountNumber", "==", normalizedAccountNumber),
      limit(opts.limitCount ?? 10)
    )
  );

  return filterDeleted(normalizeRows(snap), opts.showDeleted === true).sort(
    sortNewestFirst
  );
}

/**
 * Convenience helper for support screens where account number should resolve
 * to one business.
 */
export async function findFirstBusinessAdminByAccountNumber(
  accountNumber: string,
  opts: FindByAccountNumberOpts = {}
): Promise<AdminBusiness | null> {
  const rows = await findBusinessesAdminByAccountNumber(accountNumber, {
    ...opts,
    limitCount: 1,
  });

  return rows[0] ?? null;
}

// The backend business write trigger owns businessSearch synchronization.
// Admin UI helpers should only update businesses/{businessId}.

/**
 * Admin business list.
 *
 * Important:
 * We keep the Firestore query simple.
 *
 * We do NOT use where("isDeleted", "==", false)
 * because older docs may not have isDeleted.
 *
 * We do NOT use orderBy("updatedAt")
 * because older docs may not have updatedAt.
 *
 * We filter and sort in code to avoid unnecessary composite indexes.
 */
export function listenBusinessesAdmin(
  opts: ListenOpts,
  cb: (rows: AdminBusiness[]) => void
) {
  uidOrThrow();

  const col = collection(db, BUSINESS_COLLECTION);

  const q =
    opts.status === "all"
      ? query(col, limit(opts.limitCount ?? DEFAULT_ADMIN_LIMIT))
      : query(
          col,
          where("status", "==", opts.status),
          limit(opts.limitCount ?? DEFAULT_ADMIN_LIMIT)
        );

  return onSnapshot(
    q,
    (snap) => {
      const rows = filterDeleted(normalizeRows(snap), opts.showDeleted).sort(
        sortNewestFirst
      );

      cb(rows);
    },
    () => {
      console.warn("Admin business listener failed.");
      cb([]);
    }
  );
}

/**
 * Triage list.
 *
 * Uses status query only, then filters deleted/claimed in code.
 */
export function listenPendingUnclaimed(
  opts: { limitCount?: number; status?: "pending" | "changes_requested" },
  cb: (rows: AdminBusiness[]) => void
) {
  uidOrThrow();

  const targetStatus = opts.status ?? "pending";
  const col = collection(db, BUSINESS_COLLECTION);

  const q = query(
    col,
    where("status", "==", targetStatus),
    limit(opts.limitCount ?? DEFAULT_ADMIN_LIMIT)
  );

  return onSnapshot(
    q,
    (snap) => {
      const rows = normalizeRows(snap)
        .filter((business) => {
          return business.isDeleted !== true && business.isClaimed !== true;
        })
        .sort(sortOldestFirst);

      cb(rows);
    },
    () => {
      console.warn("Pending unclaimed business listener failed.");
      cb([]);
    }
  );
}

/**
 * Normal admin approval without claim workflow.
 * The backend business write trigger syncs businessSearch.
 */
export async function adminApprovePublish(businessId: string) {
  const uid = uidOrThrow();

  const businessPatch = {
    status: "approved" as AdminBusinessStatus,
    isPublished: true,
    isDeleted: false,
    isDeactivated: false,

    approvedAt: serverTimestamp(),
    approvedByUid: uid,

    publishedAt: serverTimestamp(),
    publishedByUid: uid,

    rejectedReason: "",
    changesRequestMessage: "",

    updatedAt: serverTimestamp(),
  };

  await updateDoc(doc(db, BUSINESS_COLLECTION, businessId), businessPatch);
}

export async function adminReject(businessId: string, reason?: string) {
  const uid = uidOrThrow();
  const cleanReason = (reason ?? "").trim();

  await updateDoc(doc(db, BUSINESS_COLLECTION, businessId), {
    status: "rejected",
    isPublished: false,

    rejectedAt: serverTimestamp(),
    rejectedByUid: uid,
    rejectedReason: cleanReason,

    changesRequestMessage: "",

    updatedAt: serverTimestamp(),
  });
}

export async function adminRequestChanges(
  businessId: string,
  message?: string
) {
  const uid = uidOrThrow();
  const cleanMessage = (message ?? "").trim();

  await updateDoc(doc(db, BUSINESS_COLLECTION, businessId), {
    status: "changes_requested",
    isPublished: false,

    changesRequestedAt: serverTimestamp(),
    changesRequestedByUid: uid,
    changesRequestMessage: cleanMessage,

    rejectedReason: "",

    updatedAt: serverTimestamp(),
  });
}

export async function adminUnpublish(businessId: string) {
  uidOrThrow();

  await updateDoc(doc(db, BUSINESS_COLLECTION, businessId), {
    status: "approved" as AdminBusinessStatus,
    isPublished: false,
    updatedAt: serverTimestamp(),
  });
}

export async function adminToggleFeatured(businessId: string, next: boolean) {
  const uid = uidOrThrow();

  const refBusiness = doc(db, BUSINESS_COLLECTION, businessId);

  if (!next) {
    await updateDoc(refBusiness, {
      isFeatured: false,
      featured: deleteField(),
      featuredAt: null,
      featuredByUid: null,
      featuredRank: DEFAULT_FEATURED_RANK,
      updatedAt: serverTimestamp(),
    });

    return;
  }

  await updateDoc(refBusiness, {
    isFeatured: true,
    featured: deleteField(),
    featuredAt: serverTimestamp(),
    featuredByUid: uid,
    featuredRank: DEFAULT_FEATURED_RANK,
    updatedAt: serverTimestamp(),
  });
}

export async function adminSoftDelete(businessId: string, next: boolean) {
  const uid = uidOrThrow();

  await updateDoc(doc(db, BUSINESS_COLLECTION, businessId), {
    isDeleted: next,

    deletedAt: next ? serverTimestamp() : null,
    deletedByUid: next ? uid : null,

    updatedAt: serverTimestamp(),
  });
}

export async function claimNextPending(opts?: {
  batch?: number;
  status?: "pending" | "changes_requested";
}) {
  const uid = uidOrThrow();
  const targetStatus = opts?.status ?? "pending";
  const readLimit = opts?.batch ?? DEFAULT_ADMIN_LIMIT;

  const q = query(
    collection(db, BUSINESS_COLLECTION),
    where("status", "==", targetStatus),
    limit(readLimit)
  );

  const snap = await getDocs(q);

  const candidates = normalizeRows(snap)
    .filter((business) => {
      return business.isDeleted !== true && business.isClaimed !== true;
    })
    .sort(sortOldestFirst);

  for (const candidate of candidates) {
    const ok = await runTransaction(db, async (tx) => {
      const refBusiness = doc(db, BUSINESS_COLLECTION, candidate.id);
      const businessSnap = await tx.get(refBusiness);

      if (!businessSnap.exists()) {
        return false;
      }

      const data = businessSnap.data() as Record<string, unknown>;

      if (data.isDeleted === true) return false;
      if (data.status !== targetStatus) return false;
      if (data.isClaimed === true) return false;

      tx.update(refBusiness, {
        isClaimed: true,
        claim: {
          claimedBy: uid,
          claimedAt: serverTimestamp(),
          claimExpiresAt: expiresInMinutes(CLAIM_MINUTES),
        },
        updatedAt: serverTimestamp(),
      });

      return true;
    });

    if (ok) {
      return candidate.id;
    }
  }

  return null;
}

export async function extendClaim(businessId: string) {
  const uid = uidOrThrow();
  const refBusiness = doc(db, BUSINESS_COLLECTION, businessId);

  await runTransaction(db, async (tx) => {
    const businessSnap = await tx.get(refBusiness);

    if (!businessSnap.exists()) {
      return;
    }

    const data = businessSnap.data() as Record<string, unknown>;
    const claim = data.claim as { claimedBy?: string | null } | undefined;

    if (claim?.claimedBy !== uid) {
      return;
    }

    tx.update(refBusiness, {
      "claim.claimExpiresAt": expiresInMinutes(CLAIM_MINUTES),
      updatedAt: serverTimestamp(),
    });
  });
}

export async function releaseClaim(businessId: string) {
  const uid = uidOrThrow();
  const refBusiness = doc(db, BUSINESS_COLLECTION, businessId);

  await runTransaction(db, async (tx) => {
    const businessSnap = await tx.get(refBusiness);

    if (!businessSnap.exists()) {
      return;
    }

    const data = businessSnap.data() as Record<string, unknown>;
    const claim = data.claim as { claimedBy?: string | null } | undefined;

    if (claim?.claimedBy !== uid) {
      return;
    }

    tx.update(refBusiness, {
      isClaimed: false,
      claim: null,
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * Claim-owned triage helper.
 *
 * Checks:
 * - admin is authenticated
 * - business is claimed by this admin
 * - claim has not expired
 *
 * Then updates businesses/{businessId}.
 * The backend business write trigger syncs businessSearch.
 */
async function withClaimOwned(
  businessId: string,
  businessPatch: Record<string, unknown>
) {
  const uid = uidOrThrow();
  const refBusiness = doc(db, BUSINESS_COLLECTION, businessId);

  await runTransaction(db, async (tx) => {
    const businessSnap = await tx.get(refBusiness);

    if (!businessSnap.exists()) {
      throw new Error("Business not found");
    }

    const data = businessSnap.data() as Record<string, unknown>;
    const claim = data.claim as
      | {
          claimedBy?: string | null;
          claimExpiresAt?: {
            toMillis?: () => number;
          };
        }
      | undefined;

    if (claim?.claimedBy !== uid) {
      throw new Error("Not claimed by you.");
    }

    const expMs = claim?.claimExpiresAt?.toMillis?.() ?? 0;

    if (expMs && expMs < Date.now()) {
      throw new Error("Claim expired. Claim again.");
    }

    tx.update(refBusiness, {
      ...businessPatch,
      isClaimed: false,
      claim: null,
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * Triage approval.
 *
 * After approval, the business write trigger syncs businessSearch.
 */
export async function triageApprovePublish(businessId: string) {
  const uid = uidOrThrow();

  await withClaimOwned(
    businessId,
    {
      status: "approved",
      isPublished: true,
      isDeleted: false,
      isDeactivated: false,

      approvedAt: serverTimestamp(),
      approvedByUid: uid,

      publishedAt: serverTimestamp(),
      publishedByUid: uid,

      rejectedReason: "",
      changesRequestMessage: "",
    }
  );
}

export async function triageReject(businessId: string, reason: string) {
  const uid = uidOrThrow();
  const cleanReason = (reason ?? "").trim();

  await withClaimOwned(
    businessId,
    {
      status: "rejected",
      isPublished: false,

      rejectedAt: serverTimestamp(),
      rejectedByUid: uid,
      rejectedReason: cleanReason,

      changesRequestMessage: "",
    }
  );
}

export async function triageRequestChanges(
  businessId: string,
  message: string
) {
  const uid = uidOrThrow();
  const cleanMessage = (message ?? "").trim();

  await withClaimOwned(
    businessId,
    {
      status: "changes_requested",
      isPublished: false,

      changesRequestedAt: serverTimestamp(),
      changesRequestedByUid: uid,
      changesRequestMessage: cleanMessage,

      rejectedReason: "",
    }
  );
}

export async function triageEditMinor(
  businessId: string,
  patch: Partial<{
    name: string;
    slug: string;
    phone: string;
    website: string | null;
    addressLine: string | null;
  }>
) {
  const { name, slug, phone, website, addressLine } = patch;

  const safePatch: Record<string, unknown> = {};

  if (typeof name === "string") {
    safePatch.name = name;
  }

  if (typeof slug === "string") {
    safePatch.slug = slug;
  }

  if (typeof phone === "string") {
    safePatch.phone = phone;
  }

  if (typeof website === "string" || website === null) {
    safePatch.website = website;
  }

  if (typeof addressLine === "string" || addressLine === null) {
    safePatch.addressLine = addressLine;
  }

  await withClaimOwned(businessId, safePatch);
}
